import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { TextFormField } from 'lib/form/TextFormField';
import { ApplicationState } from 'store/store';
import { ButtonV2 } from 'ui/ButtonV2';
import { PayBaseModal } from './PayBaseModal';

const PREFIX = 'PayTipsCustomModal';

const classes = {
    body: `${PREFIX}body`,
    input: `${PREFIX}input`,
    textfield: `${PREFIX}textfield`
};

const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.body}`]: {
        fontSize: '15px',
        fontWeight: 700
    },

    [`& .${classes.input}`]: {
        padding: theme.spacing(1.5, 2),
        paddingLeft: 0,
        textAlign: 'center'
    },

    [`& .${classes.textfield}`]: {
        width: '86px',
        margin: theme.spacing(1, 0)
    }
}));

interface PayTipsCustomModalProps {
    open: boolean;
    initialValue?: number;
    onClose: () => void;
    onSubmit: (tip: number) => void;
}

export const PayTipsCustomModal: React.FC<React.PropsWithChildren<PayTipsCustomModalProps>> = ({
    open,
    onClose,
    initialValue,
    onSubmit
}) => {
    const { t } = useTranslation();

    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const currencySymbol = React.useMemo(
        () => settings?.region?.currencySymbol ?? '£',
        [settings?.region?.currencySymbol]
    );
    const initialValues = React.useMemo(
        () => ({ tip: initialValue === 0 ? undefined : initialValue }),
        [initialValue]
    );
    const validationSchema = React.useMemo(
        () =>
            Yup.object().shape({
                tip: Yup.number()
                    .required(t('FORMS_VALIDATION_REQUIRED'))
                    .typeError(t('FORMS_VALIDATION_REQUIRED'))
            }),
        [t]
    );
    const handleSubmit = React.useCallback(
        (data: { tip?: number }) => {
            onSubmit(Number(data.tip));
        },
        [onSubmit]
    );
    const startAdornment = React.useMemo(
        () => (
            <Box>
                <Typography>{currencySymbol}</Typography>
            </Box>
        ),
        [currencySymbol]
    );
    return (
        <PayBaseModal open={!!open} onClose={onClose}>
            <StyledBox px={1} py={1.5}>
                <Formik
                    onSubmit={handleSubmit}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                >
                    {({ submitForm, isValid }) => (
                        <Form>
                            <Box display="flex" flexDirection="column" alignItems="center">
                                <Typography className={classes.body}>
                                    {t('PAT_QUICKPAY_TIPS_CUSTOM_BODY')}
                                </Typography>
                                <TextFormField
                                    className={classes.textfield}
                                    type="number"
                                    name="tip"
                                    step={0.01}
                                    InputProps={{
                                        startAdornment,
                                        autoComplete: 'off',
                                        autoFocus: true,
                                        inputMode: 'numeric'
                                    }}
                                    inputProps={{ className: classes.input }}
                                />
                                <ButtonV2 disabled={!isValid} onClick={submitForm}>
                                    {t('BUTTON_SUBMIT')}
                                </ButtonV2>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </StyledBox>
        </PayBaseModal>
    );
};
